import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Header, Menu, MenuItem, User } from '@spirit-testing/component-library';
import { AppBreadcrumbComponent } from '../breadcrumb/app-breadcrumb.component';
import { ServerUnavailableBannerComponent } from '../../components/server-unavailable-banner/server-unavailable-banner.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterOutlet } from '@angular/router';
import { HeaderColorService } from '../../services/header-color.service';
import { HeaderLogoService } from '../../services/header-logo.service';
import { DEFAULT_HEADER_NAME, HeaderNameService } from '../../services/header-name.service';
import { BrandingApiService } from '../../services/branding-api.service';
import { AccountService } from '../../account/account.service';
import { isTenantAdmin } from '../../models/user-role.model';
import { HeaderInboxBadgeService } from '../../services/header-inbox-badge.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, map, take } from 'rxjs';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    Header,
    Menu,
    TranslateModule,
    MenuItem,
    AppBreadcrumbComponent,
    ServerUnavailableBannerComponent,
    RouterOutlet,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  host: { '[class.main-layout--product-mark]': 'showsProductMark()' },
})
export class MainLayoutComponent implements OnInit {
  private brandingApiService = inject(BrandingApiService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private msalService = inject(MsalService);
  private accountService = inject(AccountService);
  protected headerColorService = inject(HeaderColorService);
  protected headerLogoService = inject(HeaderLogoService);
  protected headerNameService = inject(HeaderNameService);
  protected inboxBadge = inject(HeaderInboxBadgeService);

  user?: User;
  readonly canViewTenantSettings = signal(false);
  readonly isLoggedIn = signal(false);

  // lib-header rendert den Titel nur als Text; das fette „OFFICE“ setzt das Stylesheet dazu.
  protected readonly showsProductMark = computed(() => this.headerNameService.name() === DEFAULT_HEADER_NAME);
  protected readonly headerTitle = computed(() => (this.showsProductMark() ? 'test' : this.headerNameService.name()));

  ngOnInit(): void {
    const inProgressNone$ = this.msalBroadcastService.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
    );

    inProgressNone$
      .pipe(
        map(
          () => this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0] ?? null,
        ),
        filter((account) => account !== null),
        take(1),
      )
      .subscribe((account) => {
        const nameParts = (account.name ?? '').trim().split(' ');
        const roles = (account.idTokenClaims as { roles?: string[] })?.roles;
        const appRole = roles?.find((r) => !r.includes('.'));
        this.user = {
          id: account.localAccountId,
          firstName: nameParts[0] ?? '',
          lastName: nameParts.slice(1).join(' '),
          email: account.username,
          position: appRole,
        };
        this.isLoggedIn.set(true);
        this.inboxBadge.refresh();
      });

    inProgressNone$.pipe(take(1)).subscribe(() => {
      this.brandingApiService.getBranding().subscribe({
        next: (data) => {
          this.headerNameService.loadFromBackend(data.name);
          this.headerLogoService.loadFromBackend(data.logoPath);
          this.headerColorService.loadFromBackend(data.headerColor);
        },
        error: () => {
          this.headerLogoService.resetToDefault();
          this.headerColorService.resetToDefault();
          this.snackBar.open(this.translate.instant('branding.loadError'), undefined, {
            panelClass: 'snackbar-error',
          });
        },
      });
    });

    inProgressNone$.pipe(take(1)).subscribe(() => {
      this.accountService.getProfile().subscribe({
        next: (profile) => this.canViewTenantSettings.set(isTenantAdmin(profile.role)),
        error: () => this.canViewTenantSettings.set(false),
      });
    });
  }
}
